import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import axios from 'axios';
import pollGet from '@/utils/pollGet';
import { useCollapsedSeparators } from '../utils/useCollapsedSeparators';
import { PartialReminderBanner } from '@/components/PartialReminderBanner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { normalizeWeightInput, parseWeight } from '@/utils/weights';
import { Plus, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CivicCell = memo(({ studentId, colId, value, isReadOnly, onChange }) => {
  const [localVal, setLocalVal] = useState(value);
  const [focused, setFocused] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => { if (!focused) setLocalVal(value); }, [value, focused]);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  const handleChange = useCallback((e) => {
    const v = e.target.value.toUpperCase();
    if (v === '' || ['1', '0', 'R', 'J', 'N', 'NA'].includes(v)) {
      setLocalVal(v);
      if (!isReadOnly && v !== value && v !== 'N') {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => onChange(studentId, colId, v), 500);
      }
    }
  }, [studentId, colId, value, isReadOnly, onChange]);
  const handleBlur = useCallback(() => {
    setFocused(false);
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (localVal !== value && !isReadOnly) onChange(studentId, colId, localVal);
  }, [localVal, value, studentId, colId, isReadOnly, onChange]);
  const getColor = (v) => {
    if (v === '1') return 'bg-green-100 text-green-700';
    if (v === '0') return 'bg-red-100 text-red-700';
    if (v === 'R') return 'bg-yellow-100 text-yellow-700';
    if (v === 'J' || v === 'JO') return 'bg-blue-100 text-blue-700';
    if (v === 'NA') return 'bg-slate-200 text-slate-500';
    return 'bg-white';
  };
  if (value === 'JO' || localVal === 'JO') {
    return <div className="w-10 h-9 mx-auto flex items-center justify-center text-xs font-bold border-2 rounded-lg bg-blue-100 text-blue-700 cursor-not-allowed">JO</div>;
  }
  return (
    <input type="text" maxLength={2} value={localVal} onChange={handleChange}
      onFocus={() => setFocused(true)} onBlur={handleBlur} disabled={isReadOnly}
      className={`w-10 h-9 text-center text-xs font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 ${getColor(localVal)} ${isReadOnly ? 'cursor-not-allowed opacity-70' : ''}`} />
  );
});
CivicCell.displayName = 'CivicCell';

const CivicParticipationGrid = ({ classId, students, role, isTutor, orientadorJustifications = [], onSaveStatus, partialDates, refreshTick = 0, suspensionDates = [], conflictTick = 0, flushRef, tutorOnly = false }) => {
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [addDialog, setAddDialog] = useState(false);
  const [newCol, setNewCol] = useState({ date: '', name: '' });
  const [editDialog, setEditDialog] = useState(false);
  const [editingCol, setEditingCol] = useState(null);
  const [editColForm, setEditColForm] = useState({ date: '', name: '' });
  const [civicConflict, setCivicConflict] = useState(null);
  const [civicReprogram, setCivicReprogram] = useState(false);
  const [civicNewDate, setCivicNewDate] = useState('');
  const [viewCol, setViewCol] = useState(null);

  // Buffered (unsaved) cell edits, flushed as ONE batch delta on section change /
  // safety-net timer / leaving the screen — homogeneous with Asistencias/Tareas.
  const pendingRef = useRef({ teacher: {}, tutor: {} });
  const flushingRef = useRef(false);

  // Collapsible separators (must be at top level — before any conditional return)
  const sepState = useCollapsedSeparators(`collapsed-civic-${classId}`, grid?.columns || []);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchGrid = useCallback(async (silent = false) => {
    try {
      const res = await pollGet(`${API}/civic/class/${classId}`, { key: `civic-${classId}`, conditional: true, headers });
      if (!res.data) { if (!silent) setLoading(false); return; } // 304, nada nuevo
      const data = res.data;
      // MIGRATION: if old format (tutor_columns/teacher_columns separate), merge into columns
      if (!data.columns && (data.tutor_columns || data.teacher_columns)) {
        const tc = (data.tutor_columns || []).map(c => c.isSeparator ? c : { ...c, source: 'tutor' });
        const tch = (data.teacher_columns || []).map(c => c.isSeparator ? c : { ...c, source: 'teacher' });
        const seps = (data.separators || []);
        // Build merged columns: tutor data + teacher data, insert separators at positions
        const dataCols = [...tc.filter(c => !c.isSeparator), ...tch.filter(c => !c.isSeparator)];
        const merged = [];
        let di = 0;
        const sortedSeps = [...seps].sort((a, b) => (a.position || 0) - (b.position || 0));
        let si = 0;
        for (let i = 0; i <= dataCols.length; i++) {
          while (si < sortedSeps.length && (sortedSeps[si].position || 0) === i) {
            merged.push({ id: sortedSeps[si].id, isSeparator: true, label: sortedSeps[si].label });
            si++;
          }
          if (i < dataCols.length) merged.push(dataCols[i]);
        }
        // Also add any seps from inside teacher_columns
        const inlineSeps = tch.filter(c => c.isSeparator);
        inlineSeps.forEach(s => merged.push(s));
        data.columns = merged;
      }
      setGrid(tutorOnly ? { ...data, columns: (data.columns || []).filter(c => c.source === 'tutor') } : data);
      setLoadError(false);
    } catch (e) { console.error(e); if (!silent) setLoadError(true); } finally { if (!silent) setLoading(false); }
  }, [classId, tutorOnly]);

  useEffect(() => { fetchGrid(); }, [fetchGrid]);

  // Real-time silent refresh (parent view): re-pull without touching loading/error UI.
  useEffect(() => {
    if (refreshTick > 0) fetchGrid(true);
  }, [refreshTick]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Detect participation columns that fall on a class-suspension day (teacher only).
  const findCivicConflict = (cols) => {
    if (isTutor || role !== 'teacher') return null;
    return (cols || []).find(c => !c.isSeparator && c.date && suspensionDates.includes(c.date) && c.suspAckFor !== c.date) || null;
  };
  useEffect(() => {
    if (conflictTick > 0) {
      setCivicReprogram(false);
      setCivicNewDate('');
      setCivicConflict(findCivicConflict(grid?.columns));
    }
  }, [conflictTick]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleKeepCivic = async () => {
    const col = civicConflict;
    const cols = (grid?.columns || []).map(c => c.id === col.id ? { ...c, suspAckFor: c.date } : c);
    await saveGrid(cols, grid?.tutor_records, grid?.teacher_records, grid?.weights);
    setCivicReprogram(false); setCivicNewDate('');
    setCivicConflict(findCivicConflict(cols));
  };

  const handleReprogramCivic = async () => {
    const col = civicConflict;
    const nd = civicNewDate;
    if (!nd) { toast.error('Selecciona la nueva fecha'); return; }
    if (suspensionDates.includes(nd)) { toast.error('Esa fecha también es un día de suspensión. Elige otra.'); return; }
    const cols = (grid?.columns || []).map(c => c.id === col.id ? { ...c, date: nd, suspAckFor: undefined } : c);
    await saveGrid(cols, grid?.tutor_records, grid?.teacher_records, grid?.weights);
    toast.success('Actividad reprogramada');
    setCivicReprogram(false); setCivicNewDate('');
    setCivicConflict(findCivicConflict(cols));
  };

  const hasJO = (studentId, dateStr) => {
    if (!orientadorJustifications || !orientadorJustifications.length) return false;
    const student = students.find(s => s.id === studentId);
    if (!student) return false;
    const nameUpper = student.name.toUpperCase().trim();
    return orientadorJustifications.some(j => j.student_name?.toUpperCase().trim() === nameUpper && j.dates?.includes(dateStr));
  };

  // Save the full grid state
  const saveGrid = async (columns, tutorRecords, teacherRecords, weights, intentional = false) => {
    if (loadError) { onSaveStatus?.('error'); return; }
    const payload = {};
    // Split columns back into tutor/teacher for backend compatibility
    const tutorCols = columns.filter(c => c.source === 'tutor' && !c.isSeparator);
    const teacherCols = columns.filter(c => c.source === 'teacher' && !c.isSeparator);
    const seps = columns.filter(c => c.isSeparator);
    // Calculate separator positions in the data-only array
    let dataIdx = 0;
    const sepsWithPos = [];
    for (const col of columns) {
      if (col.isSeparator) {
        sepsWithPos.push({ id: col.id, label: col.label, position: dataIdx });
      } else {
        dataIdx++;
      }
    }

    if (isTutor) {
      try {
        onSaveStatus?.('saving');
        await axios.put(`${API}/civic/class/${classId}/tutor${intentional ? '?intentional=true' : ''}`, {
          tutor_columns: tutorCols,
          tutor_records: tutorRecords || grid?.tutor_records || {}
        }, { headers });
        onSaveStatus?.('saved');
      } catch (e) { console.error(e); onSaveStatus?.('error'); }
    } else {
      try {
        onSaveStatus?.('saving');
        await axios.put(`${API}/civic/class/${classId}/teacher${intentional ? '?intentional=true' : ''}`, {
          teacher_columns: teacherCols,
          teacher_records: teacherRecords || grid?.teacher_records || {},
          separators: sepsWithPos,
          weights: weights || grid?.weights || {}
        }, { headers });
        onSaveStatus?.('saved');
      } catch (e) { console.error(e); onSaveStatus?.('error'); }
    }
    fetchGrid();
  };

  const handleAddColumn = async () => {
    if (!newCol.date || !newCol.name.trim()) { toast.error('Fecha y nombre son requeridos'); return; }
    const colId = `civic_${Date.now()}`;
    const col = { id: colId, date: newCol.date, name: newCol.name.trim(), source: isTutor ? 'tutor' : 'teacher' };
    const columns = [...(grid?.columns || [])];
    // Insert before the last separator block or at end
    columns.push(col);
    await saveGrid(columns, grid?.tutor_records, grid?.teacher_records, grid?.weights);
    toast.success('Columna agregada');
    setAddDialog(false);
    setNewCol({ date: '', name: '' });
  };

  const openEditCol = (col) => {
    setEditingCol(col);
    setEditColForm({ date: col.date || '', name: col.name || '' });
    setEditDialog(true);
  };

  const handleEditCol = async () => {
    if (!editingCol) return;
    const columns = (grid?.columns || []).map(c => c.id === editingCol.id ? { ...c, date: editColForm.date, name: editColForm.name.trim() } : c);
    await saveGrid(columns, grid?.tutor_records, grid?.teacher_records, grid?.weights);
    toast.success('Evento actualizado');
    setEditDialog(false);
    setEditingCol(null);
  };

  const handleDeleteCol = async (colId) => {
    if (!window.confirm('¿Eliminar este evento y todos sus registros?')) return;
    const columns = (grid?.columns || []).filter(c => c.id !== colId);
    await saveGrid(columns, grid?.tutor_records, grid?.teacher_records, grid?.weights, true);
    toast.success('Evento eliminado');
  };


  const handleAddSeparator = async () => {
    if (isTutor) return;
    const columns = [...(grid?.columns || [])];
    const existingSeps = columns.filter(c => c.isSeparator);
    if (existingSeps.length >= 3) { toast.error('Máximo 3 separadores'); return; }
    const names = ['FIN P1', 'FIN P2', 'FIN P3'];
    columns.push({ id: `sep_${Date.now()}`, isSeparator: true, label: names[existingSeps.length] });
    await saveGrid(columns, grid?.tutor_records, grid?.teacher_records, grid?.weights);
    toast.success('Separador agregado');
  };

  const handleMoveSeparator = async (sepId, direction) => {
    if (isTutor) return;
    const columns = [...(grid?.columns || [])];
    const idx = columns.findIndex(c => c.id === sepId);
    if (idx < 0) return;
    const swapIdx = direction === 'left' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= columns.length) return;
    [columns[idx], columns[swapIdx]] = [columns[swapIdx], columns[idx]];
    await saveGrid(columns, grid?.tutor_records, grid?.teacher_records, grid?.weights);
  };

  const handleDeleteSeparator = async (sepId) => {
    if (isTutor) return;
    const columns = (grid?.columns || []).filter(c => c.id !== sepId);
    await saveGrid(columns, grid?.tutor_records, grid?.teacher_records, grid?.weights, true);
  };

  const handleCellChange = useCallback((studentId, colId, value) => {
    if (!grid || loadError) return;
    const col = (grid.columns || []).find(c => c.id === colId);
    if (!col) return;
    const isTutorCol = col.source === 'tutor';
    if (isTutorCol && isTutor) {
      setGrid(prev => ({ ...prev, tutor_records: { ...(prev.tutor_records || {}), [studentId]: { ...((prev.tutor_records || {})[studentId] || {}), [colId]: value } } }));
      if (!pendingRef.current.tutor[studentId]) pendingRef.current.tutor[studentId] = {};
      pendingRef.current.tutor[studentId][colId] = value;
      onSaveStatus?.('unsaved');
    } else if (!isTutorCol && !isTutor) {
      setGrid(prev => ({ ...prev, teacher_records: { ...(prev.teacher_records || {}), [studentId]: { ...((prev.teacher_records || {})[studentId] || {}), [colId]: value } } }));
      if (!pendingRef.current.teacher[studentId]) pendingRef.current.teacher[studentId] = {};
      pendingRef.current.teacher[studentId][colId] = value;
      onSaveStatus?.('unsaved');
    }
  }, [grid, classId, isTutor, loadError, onSaveStatus]);

  // ---- Batch flush of buffered cell edits ----
  const hasPendingCells = () =>
    Object.keys(pendingRef.current.teacher).length > 0 || Object.keys(pendingRef.current.tutor).length > 0;
  const buildCells = (snap) => {
    const arr = [];
    Object.entries(snap).forEach(([sid, cols]) => Object.entries(cols).forEach(([cid, v]) => arr.push({ student_id: sid, col_id: cid, value: v })));
    return arr;
  };
  const flushCells = useCallback(async () => {
    if (flushingRef.current) return true;
    if (!hasPendingCells()) return true;
    flushingRef.current = true;
    const teacherSnap = pendingRef.current.teacher; pendingRef.current.teacher = {};
    const tutorSnap = pendingRef.current.tutor; pendingRef.current.tutor = {};
    const teacherCells = buildCells(teacherSnap);
    const tutorCells = buildCells(tutorSnap);
    const token = localStorage.getItem('token');
    const h = { Authorization: `Bearer ${token}` };
    let ok = true;
    try {
      onSaveStatus?.('saving');
      if (teacherCells.length) await axios.patch(`${API}/civic/class/${classId}/cells`, { scope: 'teacher', cells: teacherCells }, { headers: h });
      if (tutorCells.length) await axios.patch(`${API}/civic/class/${classId}/cells`, { scope: 'tutor', cells: tutorCells }, { headers: h });
      onSaveStatus?.('saved');
    } catch (e) {
      ok = false;
      Object.entries(teacherSnap).forEach(([sid, cols]) => { pendingRef.current.teacher[sid] = { ...cols, ...(pendingRef.current.teacher[sid] || {}) }; });
      Object.entries(tutorSnap).forEach(([sid, cols]) => { pendingRef.current.tutor[sid] = { ...cols, ...(pendingRef.current.tutor[sid] || {}) }; });
      onSaveStatus?.('error');
    } finally {
      flushingRef.current = false;
    }
    return ok;
  }, [classId, onSaveStatus]);

  const flushCellsRef = useRef(flushCells);
  useEffect(() => { flushCellsRef.current = flushCells; }, [flushCells]);
  // Expose flush to the parent (ClassView) so it can flush on tab change.
  useEffect(() => {
    if (flushRef) flushRef.current = () => flushCellsRef.current();
    return () => { if (flushRef) flushRef.current = null; };
  }, [flushRef]);
  // Safety net + save on leaving (unmount) + best-effort save on tab/browser close.
  useEffect(() => {
    const id = setInterval(() => { if (hasPendingCells()) flushCellsRef.current(); }, 15000);
    const onBeforeUnload = () => {
      if (!hasPendingCells()) return;
      try {
        const token = localStorage.getItem('token');
        const tc = buildCells(pendingRef.current.teacher);
        const tu = buildCells(pendingRef.current.tutor);
        const hdr = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
        if (tc.length) fetch(`${API}/civic/class/${classId}/cells`, { method: 'PATCH', keepalive: true, headers: hdr, body: JSON.stringify({ scope: 'teacher', cells: tc }) });
        if (tu.length) fetch(`${API}/civic/class/${classId}/cells`, { method: 'PATCH', keepalive: true, headers: hdr, body: JSON.stringify({ scope: 'tutor', cells: tu }) });
      } catch (e) { /* ignore */ }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      clearInterval(id);
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (hasPendingCells()) flushCellsRef.current();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  const handleWeightChange = async (key, raw) => {
    if (loadError) return;
    const val = normalizeWeightInput(raw);
    if (val === null) return;
    const weights = { ...(grid?.weights || {}), [key]: val };
    setGrid(prev => ({ ...prev, weights }));
    try {
      onSaveStatus?.('saving');
      await axios.put(`${API}/civic/class/${classId}/teacher`, { weights }, { headers });
      onSaveStatus?.('saved');
    } catch (e) { console.error(e); onSaveStatus?.('error'); }
  };

  if (loading) return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-teal-500" /></div>;

  if (loadError) {
    return (
      <div className="py-8 text-center" data-testid="civic-load-error">
        <p className="text-red-600 font-semibold mb-3">No se pudieron cargar los datos de participación. Edición bloqueada para proteger la información.</p>
        <Button onClick={() => { setLoading(true); fetchGrid(); }} className="bg-red-500 hover:bg-red-600 text-white" data-testid="civic-reload-btn">Reintentar</Button>
      </div>
    );
  }

  // Build display with cumulative % per parcial (same as tasks)
  const columns = grid?.columns || [];
  const buildDisplay = () => {
    const result = [];
    let block = [];
    let pi = 0;
    for (const col of columns) {
      if (col.isSeparator) {
        pi++;
        result.push({ _isCumulative: true, _pi: pi, _colIds: block.map(c => c.id), label: `% P${pi}` });
        result.push(col);
        block = [];
      } else {
        result.push(col);
        block.push(col);
      }
    }
    if (block.length > 0) {
      pi++;
      result.push({ _isCumulative: true, _pi: pi, _colIds: block.map(c => c.id), label: columns.some(c => c.isSeparator) ? `% P${pi}` : '% Total' });
    }
    return result;
  };
  const display = buildDisplay();
  const visibleDisplay = display.filter(c => !sepState.hiddenColIds.has(c.id));
  const weights = grid?.weights || {};
  const pKeys = ['parcial_1', 'parcial_2', 'parcial_3'];

  const calcPct = (studentId, colIds) => {
    const tutorRec = grid?.tutor_records?.[studentId] || {};
    const teacherRec = grid?.teacher_records?.[studentId] || {};
    let total = 0, attended = 0;
    colIds.forEach(id => {
      const v = (tutorRec[id] || teacherRec[id] || '').toUpperCase();
      if (v === '0' || v === '1' || v === 'R') { total++; if (v !== '0') attended++; }
    });
    return total === 0 ? null : Math.round((attended / total) * 100);
  };

  const dataCols = columns.filter(c => !c.isSeparator);

  return (
    <div data-testid="civic-participation-section">
      <Dialog open={!!viewCol} onOpenChange={(o) => { if (!o) setViewCol(null); }}>
        <DialogContent className="bg-white max-w-md" data-testid="civic-view-col-dialog">
          <DialogHeader>
            <DialogTitle className="text-teal-700">Evento del tutor</DialogTitle>
          </DialogHeader>
          {viewCol && (
            <div className="space-y-2">
              <p className="text-base font-semibold text-slate-900 break-words whitespace-pre-wrap" data-testid="civic-view-col-name">{viewCol.name}</p>
              {viewCol.date && <p className="text-sm text-slate-500">Fecha: {viewCol.date.split('-').reverse().join('/')}</p>}
              <p className="text-xs text-slate-400">Agregado desde Grupo Tutorado. Solo el tutor puede editarlo o eliminarlo.</p>
            </div>
          )}
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setViewCol(null)} data-testid="civic-view-col-close">Cerrar</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!civicConflict} onOpenChange={(o) => { if (!o) { setCivicConflict(null); setCivicReprogram(false); } }}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-amber-700">Coincide con día de suspensión</DialogTitle>
          </DialogHeader>
          {civicConflict && (
            <div className="space-y-4">
              <p className="text-sm text-slate-700">
                La actividad <strong>{civicConflict.name}</strong> (fecha {civicConflict.date}) está programada en un día de <strong>suspensión de clases</strong>. Puedes reprogramarla o mantenerla.
              </p>
              {!civicReprogram ? (
                <div className="flex flex-col gap-2">
                  <Button onClick={() => setCivicReprogram(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="conflict-civic-reprogram">Reprogramar</Button>
                  <Button variant="outline" onClick={handleKeepCivic} data-testid="conflict-civic-keep">Mantener y no avisar para esta actividad</Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Nueva fecha</Label>
                  <Input type="date" value={civicNewDate} onChange={(e) => setCivicNewDate(e.target.value)} data-testid="conflict-civic-newdate" />
                  <div className="flex gap-2 pt-1">
                    <Button variant="outline" className="flex-1" onClick={() => setCivicReprogram(false)}>Cancelar</Button>
                    <Button className="flex-1 bg-orange-500 hover:bg-orange-600" onClick={handleReprogramCivic} data-testid="conflict-civic-confirm">Confirmar</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {role === 'teacher' && !isTutor && (
        <PartialReminderBanner
          partialDates={partialDates}
          separatorCount={(grid?.columns || []).filter(c => c.isSeparator).length}
          sectionLabel="Participación"
        />
      )}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-slate-900">Participación Cívica, Cultural y Deportiva</h2>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setAddDialog(true)} className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="civic-add-col">
            <Plus className="w-4 h-4 mr-1" /> Agregar Evento
          </Button>
          {!isTutor && (
            <Button size="sm" variant="outline" onClick={handleAddSeparator} data-testid="civic-add-sep">
              Separador de Parcial
            </Button>
          )}
        </div>
      </div>
      <div className="flex gap-1 text-xs flex-wrap mb-4">
        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">1 = Asistencia</span>
        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded">0 = Falta</span>
        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">R = Retardo</span>
        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">J = Justificado</span>
        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">JO = Justif. Orientador</span>
        <span className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded">NA = No Aplica</span>
      </div>

      {dataCols.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-slate-500">Sin eventos registrados. Agrega uno con el botón "Agregar Evento".</p>
        </Card>
      ) : (
        <Card className="p-3 overflow-x-auto">
          <table className="border-collapse min-w-max">
            <thead className="grid-sticky-header sticky top-0 z-20">
              <tr className="border-b-2 border-slate-300 bg-slate-50">
                <th className="sticky left-0 z-30 bg-slate-50 text-left py-2 px-2 text-xs font-semibold border-r-2 border-slate-300 min-w-[100px] max-w-[120px]">Alumno</th>
                {visibleDisplay.map((col, i) => (
                  col._isCumulative ? (
                    <th key={`cum-${i}`} className="text-center py-2 px-1 text-xs font-bold text-blue-700 bg-blue-50 border-r min-w-[55px]">{col.label}</th>
                  ) : col.isSeparator ? (
                    <th key={`sep-${i}`}
                      onClick={() => sepState.toggle(col.id)}
                      title={sepState.isCollapsed(col.id) ? 'Mostrar parcial' : 'Ocultar parcial'}
                      className={`text-center py-1 px-0 text-[8px] font-bold text-purple-700 border-r ${sepState.isCollapsed(col.id) ? 'min-w-[40px] max-w-[40px] bg-purple-200' : 'min-w-[28px] max-w-[28px] bg-purple-100'} cursor-pointer hover:bg-purple-300`}
                      style={sepState.isCollapsed(col.id) ? {} : { backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(147,51,234,0.15) 2px, rgba(147,51,234,0.15) 4px)' }}>
                      <div className="flex flex-col items-center gap-0">
                        <span className="truncate w-full">{col.label}</span>
                        {sepState.isCollapsed(col.id) ? (
                          <span className="text-purple-700 text-[9px] font-bold mt-0.5">▶</span>
                        ) : (!isTutor && (
                          <div className="flex flex-col items-center gap-0 text-[10px]">
                            <button onClick={(e) => { e.stopPropagation(); handleMoveSeparator(col.id, 'left'); }} className="text-purple-400 hover:text-purple-700 leading-none">&#9664;</button>
                            <button onClick={(e) => { e.stopPropagation(); handleDeleteSeparator(col.id); }} className="text-purple-400 hover:text-red-600 leading-none">&#10005;</button>
                            <button onClick={(e) => { e.stopPropagation(); handleMoveSeparator(col.id, 'right'); }} className="text-purple-400 hover:text-purple-700 leading-none">&#9654;</button>
                          </div>
                        ))}
                      </div>
                    </th>
                  ) : (
                    <th key={i}
                      onClick={col.source === 'tutor' && !isTutor ? () => setViewCol(col) : undefined}
                      title={col.source === 'tutor' && !isTutor ? 'Ver nombre completo' : undefined}
                      data-testid={`civic-col-header-${col.id}`}
                      className={`text-center py-2 px-1 text-xs border-r min-w-[50px] max-w-[55px] bg-slate-50 ${col.source === 'tutor' && !isTutor ? 'cursor-pointer hover:bg-teal-50' : ''}`}>
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="font-bold text-[10px]">{col.date ? col.date.split('-').reverse().slice(0,2).join('/') : ''}</span>
                        <span
                          className={`text-[9px] truncate max-w-[45px] cursor-pointer ${col.source === 'tutor' ? 'text-teal-600' : 'text-orange-600'}`}
                          title={col.name}
                          data-testid={`civic-col-name-${col.id}`}
                        >{col.name}</span>
                        {((col.source === 'tutor' && isTutor) || (col.source === 'teacher' && !isTutor)) && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <button onClick={() => openEditCol(col)} className="text-blue-500 hover:text-blue-700" title="Editar">
                              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
                            </button>
                            <button onClick={() => handleDeleteCol(col.id)} className="text-red-500 hover:text-red-700 text-xs" title="Eliminar">&#10005;</button>
                          </div>
                        )}
                      </div>
                    </th>
                  )
                ))}
              </tr>
            </thead>
            <tbody>
              {students.map(student => {
                const tutorRec = grid?.tutor_records?.[student.id] || {};
                const teacherRec = grid?.teacher_records?.[student.id] || {};
                return (
                  <tr key={student.id} className="border-b hover:bg-slate-50">
                    <td className="sticky left-0 z-10 bg-white py-1 px-2 text-xs font-medium border-r-2 border-slate-300 min-w-[100px] max-w-[120px] break-words whitespace-normal">
                      {student.name}
                      {student.matricula && <span className="block text-[9px] text-slate-400 font-normal">Mat: {student.matricula}</span>}
                    </td>
                    {visibleDisplay.map((col, ci) => {
                      if (col._isCumulative) {
                        const pct = calcPct(student.id, col._colIds);
                        const clr = pct === null ? 'text-slate-400' : pct >= 80 ? 'text-green-700 bg-green-50' : pct >= 60 ? 'text-yellow-700 bg-yellow-50' : 'text-red-700 bg-red-50';
                        return <td key={`cum-${ci}`} className={`text-center py-1 px-1 border-r text-xs font-bold ${clr}`}>{pct !== null ? `${pct}%` : '-'}</td>;
                      }
                      if (col.isSeparator) {
                        const isCol = sepState.isCollapsed(col.id);
                        return <td key={`sep-${ci}`} className={`border-r ${isCol ? 'min-w-[40px] bg-purple-200' : 'min-w-[28px] bg-purple-50'}`}></td>;
                      }
                      const isTutorCol = col.source === 'tutor';
                      const rec = isTutorCol ? tutorRec : teacherRec;
                      let cellValue = rec[col.id] || '';
                      if (col.date && hasJO(student.id, col.date)) cellValue = 'JO';
                      const canEdit = (isTutorCol && isTutor) || (!isTutorCol && !isTutor);
                      return (
                        <td key={ci} className="text-center py-1 px-0 border-r">
                          <CivicCell studentId={student.id} colId={col.id} value={cellValue} isReadOnly={!canEdit} onChange={handleCellChange} />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Calificación por Participación - always show 3 parcials */}
          {!isTutor && (
            <div className="mt-4 border-t-2 border-indigo-100 pt-3">
              <h3 className="text-base font-semibold text-indigo-700 mb-2">Calificación por Participación (sobre 10)</h3>
              <div className="flex flex-wrap gap-3 mb-3">
                {[0, 1, 2].map(i => (
                  <div key={i} className="flex items-center gap-1">
                    <Label className="text-xs text-indigo-600">Parcial {i+1} (%):</Label>
                    <input type="text" inputMode="decimal" maxLength={5} value={weights[pKeys[i]] || ''} onChange={(e) => handleWeightChange(pKeys[i], e.target.value)} className="w-16 h-7 px-2 text-xs border border-input rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400" data-testid={`civic-weight-p${i + 1}`} />
                  </div>
                ))}
              </div>
              {pKeys.some(k => weights[k] && weights[k] !== '') && (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead className="grid-sticky-header sticky top-0 z-20">
                      <tr className="border-b-2 border-indigo-200 bg-indigo-50">
                        <th className="sticky left-0 top-0 z-30 bg-indigo-50 text-left py-2 px-3 font-semibold text-indigo-700 min-w-[100px]">Alumno</th>
                        {[0, 1, 2].map(i => {
                          const w = parseWeight(weights[pKeys[i]]);
                          if (w <= 0) return null;
                          return <th key={i} className="text-center py-2 px-2 text-xs font-semibold text-indigo-700">Parcial {i+1} ({w}%)</th>;
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {students.map(student => {
                        const cumCols = display.filter(c => c._isCumulative);
                        return (
                          <tr key={student.id} className="border-b hover:bg-slate-50">
                            <td className="py-1.5 px-3 font-medium text-slate-900 text-xs">
                              {student.name}
                              {student.matricula && <span className="block text-[9px] text-slate-400 font-normal">Mat: {student.matricula}</span>}
                            </td>
                            {[0, 1, 2].map(i => {
                              const w = parseWeight(weights[pKeys[i]]);
                              if (w <= 0) return null;
                              const cum = cumCols[i];
                              if (!cum) return <td key={i} className="text-center py-1.5 px-2 text-slate-400 text-xs">--</td>;
                              const pct = calcPct(student.id, cum._colIds);
                              if (pct === null) return <td key={i} className="text-center py-1.5 px-2 text-slate-400 text-xs">--</td>;
                              const grade = (pct / 100) * (w / 100) * 10;
                              const clr = grade >= (w/100*8) ? 'text-green-700 bg-green-50' : grade >= (w/100*6) ? 'text-yellow-700 bg-yellow-50' : 'text-red-700 bg-red-50';
                              return <td key={i} className={`text-center py-1.5 px-2 font-bold text-xs ${clr} rounded`}>{grade.toFixed(2)}</td>;
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Agregar Evento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Fecha</Label><Input type="date" value={newCol.date} onChange={e => setNewCol({ ...newCol, date: e.target.value })} required noDebounce /></div>
            <div><Label>Nombre del evento</Label><Input value={newCol.name} onChange={e => setNewCol({ ...newCol, name: e.target.value })} placeholder="Ej: Desfile patrio" noDebounce /></div>
            <Button onClick={handleAddColumn} className="w-full bg-teal-500 hover:bg-teal-600">Agregar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialog} onOpenChange={(v) => { setEditDialog(v); if (!v) setEditingCol(null); }}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Editar Evento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Fecha</Label><Input type="date" value={editColForm.date} onChange={e => setEditColForm({ ...editColForm, date: e.target.value })} noDebounce /></div>
            <div><Label>Nombre</Label><Input value={editColForm.name} onChange={e => setEditColForm({ ...editColForm, name: e.target.value })} noDebounce /></div>
            <Button onClick={handleEditCol} className="w-full bg-teal-500 hover:bg-teal-600">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CivicParticipationGrid;
